import Vue from 'vue';
import { v4 as uuid } from 'uuid';

export default {
  // ============================================
  // Record mutations (API-backed page documents)
  // ============================================

  // Set a single record from API response
  setRecord: (state, record) => {
    Vue.set(state.recordsById, record.id, record);
  },

  // Set multiple records (e.g., from list endpoint)
  setRecords: (state, records) => {
    records.forEach((record) => {
      Vue.set(state.recordsById, record.id, record);
    });
  },

  // Update a record (partial update)
  updateRecord: (state, { recordId, data }) => {
    const record = state.recordsById[recordId];
    if (!record) return;
    Vue.set(state.recordsById, recordId, { ...record, ...data });
  },

  // Remove a record
  removeRecord: (state, { recordId }) => {
    Vue.delete(state.recordsById, recordId);
  },

  // ============================================
  // Page lifecycle (in-memory page content)
  // ============================================

  createPage: (state, { pageId, meta = {}, recordId = null }) => {
    Vue.set(state.pagesById, pageId, {
      id: pageId,
      recordId, // Links to API record, null for local-only pages
      created_at: Date.now(),
      updated_at: Date.now(),
      meta,
    });
    // Initialize root children array
    Vue.set(state.childrenByParent, `root_${pageId}`, []);
  },

  // Update page's recordId (after first save to API)
  setPageRecordId: (state, { pageId, recordId }) => {
    const page = state.pagesById[pageId];
    if (page) {
      page.recordId = recordId;
    }
  },

  deletePage: (state, { pageId }) => {
    // Remove all nodes belonging to this page
    Object.keys(state.nodesById).forEach((nodeId) => {
      if (state.nodesById[nodeId].pageId === pageId) {
        Vue.delete(state.nodesById, nodeId);
      }
    });
    // Clean up childrenByParent entries
    Object.keys(state.childrenByParent).forEach((key) => {
      if (key.endsWith(pageId) || state.nodesById[key]?.pageId === pageId) {
        Vue.delete(state.childrenByParent, key);
      }
    });
    Vue.delete(state.pagesById, pageId);
  },

  setActivePage: (state, pageId) => {
    state.activePageId = pageId;
  },

  // Node operations - localized reactivity
  insertNode: (state, { node }) => {
    const nodeId = node.id || uuid();
    const parentKey = node.parentId || `root_${node.pageId}`;

    // Node types that are draggable/droppable by default
    const draggableTypes = ['row', 'col'];
    const droppableTypes = ['row', 'col'];

    // Determine defaults based on node type
    const defaultDraggable = draggableTypes.includes(node.nodeType);
    const defaultDroppable = droppableTypes.includes(node.nodeType);

    Vue.set(state.nodesById, nodeId, {
      ...node,
      id: nodeId,
      draggable: node.draggable ?? defaultDraggable,
      droppable: node.droppable ?? defaultDroppable,
    });

    // Initialize children array for this node
    if (!state.childrenByParent[nodeId]) {
      Vue.set(state.childrenByParent, nodeId, []);
    }

    // Add to parent's children array (in-place mutation for stability)
    const siblings = state.childrenByParent[parentKey] || [];
    if (node.afterId) {
      const afterIndex = siblings.indexOf(node.afterId);
      siblings.splice(afterIndex + 1, 0, nodeId);
    } else {
      siblings.push(nodeId);
    }
    Vue.set(state.childrenByParent, parentKey, siblings);
  },

  patchNodeProps: (state, { nodeId, props }) => {
    const node = state.nodesById[nodeId];
    if (!node) return;
    // In-place mutation for minimal reactivity
    Object.keys(props).forEach((key) => {
      Vue.set(node.props, key, props[key]);
    });
    node.updated_at = Date.now();
  },

  appendChunk: (state, { nodeId, text }) => {
    const node = state.nodesById[nodeId];
    if (!node || !node.props.chunks) return;
    node.props.chunks.push(text);
  },

  moveNode: (state, { nodeId, newParentId, afterId }) => {
    const node = state.nodesById[nodeId];
    if (!node) return;

    const oldParentKey = node.parentId || `root_${node.pageId}`;
    const newParentKey = newParentId || `root_${node.pageId}`;

    // Remove from old parent
    const oldSiblings = state.childrenByParent[oldParentKey];
    const oldIndex = oldSiblings.indexOf(nodeId);
    if (oldIndex > -1) oldSiblings.splice(oldIndex, 1);

    // Add to new parent
    const newSiblings = state.childrenByParent[newParentKey] || [];
    if (afterId) {
      const afterIndex = newSiblings.indexOf(afterId);
      newSiblings.splice(afterIndex + 1, 0, nodeId);
    } else {
      newSiblings.push(nodeId);
    }

    node.parentId = newParentId;
  },

  removeNode: (state, { nodeId }) => {
    const node = state.nodesById[nodeId];
    if (!node) return;

    // Note: recursive removal handled in action, not mutation
    // Remove from parent's children
    const parentKey = node.parentId || `root_${node.pageId}`;
    const siblings = state.childrenByParent[parentKey];
    if (siblings) {
      const index = siblings.indexOf(nodeId);
      if (index > -1) siblings.splice(index, 1);
    }

    Vue.delete(state.childrenByParent, nodeId);
    Vue.delete(state.nodesById, nodeId);
  },

  // Component registration
  registerComponent: (state, { nodeType, component }) => {
    Vue.set(state.registeredComponents, nodeType, component);
  },

  // Batching
  queueUpdate: (state, update) => {
    state.updateQueue.push(update);
  },

  clearUpdateQueue: (state) => {
    state.updateQueue = [];
  },

  setLoading: (state, loading) => {
    state.loading = loading;
  },

  setSaving: (state, saving) => {
    state.saving = saving;
  },
};
